/**
 * Endpoints de **Autoservicio → Saldos a favor**
 * (`/api/logistica/saldos-autoservicio/*`, que se conserva por compatibilidad).
 *
 * El listado y la devolución viven en el servicio de autoservicio (la API de
 * EDSuite es el puente que pone el `X-POS-Code`); sólo los turnos abiertos se
 * leen de la base local.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  CodigosRespuesta,
  DatosDevolucion,
  DatosVigencia,
  DevolucionRespuesta,
  FiltrosSaldosAutoservicio,
  TurnoAutoservicio,
  VigenciaRespuesta,
} from './saldos-autoservicio.types';

export const saldosAutoservicioKeys = {
  codigos: (filtros: FiltrosSaldosAutoservicio) =>
    ['logistica', 'saldos-autoservicio', 'codigos', filtros] as const,
  turnos: ['logistica', 'saldos-autoservicio', 'turnos'] as const,
};

/** Arma la cadena de consulta del listado. */
function cadenaDe(filtros: FiltrosSaldosAutoservicio): string {
  const parametros = new URLSearchParams();

  if (filtros.estado) parametros.set('estado', filtros.estado);
  parametros.set('conSaldo', filtros.conSaldo ? 'true' : 'false');
  if (filtros.desde) parametros.set('desde', filtros.desde);
  if (filtros.hasta) parametros.set('hasta', filtros.hasta);
  if (filtros.expiraDesde) parametros.set('expiraDesde', filtros.expiraDesde);
  if (filtros.expiraHasta) parametros.set('expiraHasta', filtros.expiraHasta);
  parametros.set('page', String(filtros.page));
  parametros.set('pageSize', String(filtros.pageSize));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const saldosAutoservicioApi = {
  codigos: (
    filtros: FiltrosSaldosAutoservicio,
    signal?: AbortSignal,
  ): Promise<CodigosRespuesta> =>
    apiClient.get<CodigosRespuesta>(
      `/api/logistica/saldos-autoservicio/codigos${cadenaDe(filtros)}`,
      { signal },
    ),

  turnos: (signal?: AbortSignal): Promise<{ items: TurnoAutoservicio[] }> =>
    apiClient.get<{ items: TurnoAutoservicio[] }>(
      '/api/logistica/saldos-autoservicio/turnos',
      { signal },
    ),

  devolver: (datos: DatosDevolucion): Promise<DevolucionRespuesta> =>
    apiClient.post<DevolucionRespuesta>('/api/logistica/saldos-autoservicio/devolucion', {
      body: datos,
    }),

  /** Amplía (o renueva) la vigencia de un código; sólo extiende hacia adelante. */
  ampliarVigencia: (datos: DatosVigencia): Promise<VigenciaRespuesta> =>
    apiClient.post<VigenciaRespuesta>(
      `/api/logistica/saldos-autoservicio/codigos/${encodeURIComponent(datos.codigo)}/vigencia`,
      { body: { fecha: datos.fecha, motivo: datos.motivo, operador: datos.operador } },
    ),
};

/** Códigos de saldo del filtro pedido. */
export function useCodigosSaldo(filtros: FiltrosSaldosAutoservicio) {
  return useQuery({
    queryKey: saldosAutoservicioKeys.codigos(filtros),
    queryFn: ({ signal }) => saldosAutoservicioApi.codigos(filtros, signal),
    // `placeholderData` (el helper de React Query v5, no una función que devuelva
    // `undefined`: eso deja la consulta en «success» con datos vacíos y el error
    // del servicio nunca llega a pintarse).
    placeholderData: keepPreviousData,
  });
}

/** Turnos abiertos a los que se puede imputar la devolución. */
export function useTurnosAutoservicio(activo: boolean) {
  return useQuery({
    queryKey: saldosAutoservicioKeys.turnos,
    queryFn: ({ signal }) => saldosAutoservicioApi.turnos(signal),
    enabled: activo,
    staleTime: 30_000,
  });
}

/** Registra una devolución y refresca el listado de códigos. */
export function useDevolucionSaldo() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: DatosDevolucion) => saldosAutoservicioApi.devolver(datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['logistica', 'saldos-autoservicio', 'codigos'] });
    },
  });
}

/**
 * Amplía la vigencia de un código y refresca el listado.
 *
 * Al renovar, un código EXPIRADO vuelve a ACTIVO, así que la fila cambia de
 * estado (y deja de estar «vencida») sin que el usuario recargue nada.
 */
export function useAmpliarVigencia() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: DatosVigencia) => saldosAutoservicioApi.ampliarVigencia(datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['logistica', 'saldos-autoservicio', 'codigos'] });
    },
  });
}
